'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { ArrowRight, ArrowUpRight, Building2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { generateIndustryUrl, generateCategoryInternalPath } from '@/lib/industry-utils';
import {
  getCategoryLabel,
  naceToCategory,
  naceToSubcategory,
} from '@/lib/industry-categories';
import { generateCompanySlug } from '@/lib/company-utils';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer } from '@/components/ui/chart';
import { Container } from '@/components/common/container';
import { LocaleLink } from '@/components/locale-link';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';

interface StatsItem {
  naceCode: string;
  naceDescription: string;
  totalRevenue: number;
  totalProfit: number;
  totalBalanceSum: number;
  companyCount: number;
  ancestors?: { code: string; description: string; level: number }[];
}

interface ChildItem {
  code: string;
  description: string;
  level: number;
  companyCount: number;
  totalRevenue: number;
}

interface TopCompany {
  name: string;
  euId: string;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  sitz: string | null;
  revenue: number;
  marketShare: number;
  naceCodes: string[];
  logoUrl: string | null;
}

interface KeywordItem {
  category: 'product' | 'risk' | 'opportunity';
  keyword: string;
  mention_count: number;
}

interface Props {
  initialStats: StatsItem | null;
  initialChildren: ChildItem[] | null;
  initialTopCompanies: TopCompany[] | null;
  // Legacy props kept for page.tsx API compatibility — unused in this layout.
  initialTrendingCompanies?: unknown;
}

const PIE_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#ef4444',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

export default function IndustryDetailPageClient({
  initialStats,
  initialChildren,
  initialTopCompanies,
}: Props) {
  const params = useParams();
  // The code comes from initialStats (server-resolved) when available — this
  // component is mounted both from /[catSlug]/[subOrCode]/[code] (where
  // `params.code` exists) and from /[catSlug]/[subOrCode] when subOrCode is a
  // NACE code (where the URL param is `subOrCode`, not `code`).
  const code =
    initialStats?.naceCode ||
    (params.code as string | undefined) ||
    (params.subOrCode as string | undefined) ||
    '';
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);

  const stats = initialStats;
  const children = initialChildren ?? [];
  const topCompanies = useMemo(() => initialTopCompanies ?? [], [initialTopCompanies]);

  const [keywords, setKeywords] = useState<KeywordItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/industries/sector-data?code=${encodeURIComponent(code)}`,
        );
        if (!res.ok || cancelled) return;
        const json = await res.json();
        if (!cancelled) {
          setKeywords(json.data?.keywords ?? []);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const categoryKey = naceToCategory(code);
  const categoryLabel = categoryKey
    ? getCategoryLabel(categoryKey, locale)
    : null;
  const subcategoryKey = naceToSubcategory(code);
  const subcategoryLabel =
    subcategoryKey && subcategoryKey !== categoryKey
      ? getCategoryLabel(subcategoryKey, locale)
      : null;

  const pieData = useMemo(() => {
    const companies = topCompanies
      .filter((c) => c.revenue > 0 && c.marketShare > 0)
      .slice(0, 10);
    const topShare = companies.reduce((sum, c) => sum + c.marketShare, 0);
    const othersShare = Math.max(0, 100 - topShare);
    const slices: {
      name: string;
      value: number;
      revenue: number;
      color: string;
      href: string | null;
      logoUrl: string | null;
      isMore?: boolean;
    }[] = companies.map((c, i) => ({
      name: c.name,
      value: Math.round(c.marketShare * 100) / 100,
      revenue: c.revenue,
      color: PIE_COLORS[i % PIE_COLORS.length],
      href: generateCompanySlug({
        name: c.name,
        registerCourt: c.registerCourt,
        registerType: c.registerType,
        registerId: c.registerId,
      }, locale as Locale),
      logoUrl: c.logoUrl ?? null,
    }));
    if (othersShare > 0.5) {
      slices.push({
        name: locale === 'de' ? 'Mehr anzeigen' : 'See More',
        value: Math.round(othersShare * 100) / 100,
        revenue: 0,
        color: '#94a3b8',
        href: `${localizePathname('/search', locale as Locale)}?naceCode=${encodeURIComponent(code)}`,
        logoUrl: null,
        isMore: true,
      });
    }
    return slices;
  }, [topCompanies, locale, code]);

  const visibleChildren = children.filter((c) => c.companyCount > 0);

  return (
    <>
    <Container className="pt-4 pb-2">
      <nav
        className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
        aria-label="Breadcrumb"
      >
        <LocaleLink
          href="/industry"
          className="hover:text-foreground transition-colors"
        >
          {locale === 'de' ? 'Branchen' : 'Industries'}
        </LocaleLink>
        {categoryKey && categoryLabel && (
          <>
            <ChevronRight size={14} className="text-muted-foreground/50" />
            <LocaleLink
              href={generateCategoryInternalPath(categoryKey, locale)}
              className="hover:text-foreground transition-colors"
            >
              {categoryLabel}
            </LocaleLink>
          </>
        )}
        {subcategoryKey && subcategoryLabel && (
          <>
            <ChevronRight size={14} className="text-muted-foreground/50" />
            <LocaleLink
              href={generateCategoryInternalPath(subcategoryKey, locale)}
              className="hover:text-foreground transition-colors"
            >
              {subcategoryLabel}
            </LocaleLink>
          </>
        )}
        <ChevronRight size={14} className="text-muted-foreground/50" />
        <span className="text-foreground font-medium">{stats?.naceDescription || code}</span>
      </nav>
    </Container>
    <Container className="pb-12 flex flex-col gap-10">
      <div className="flex flex-col gap-3 pr-5 py-3">
        <h1 className="text-base sm:text-2xl font-bold tracking-tight text-foreground">
          {stats?.naceDescription || code}
        </h1>
        <p className="text-muted-foreground text-base font-light">
          WZ 2025 Code: {code}
        </p>
        {stats && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
                {t('common.labels.revenue')}
              </span>
              <span className="font-medium">
                {fmt.compactCurrency.format(stats.totalRevenue)}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
                {t('common.labels.profit')}
              </span>
              <span
                className={cn(
                  'font-medium',
                  stats.totalProfit >= 0
                    ? 'text-green-700 dark:text-green-500'
                    : 'text-red-700 dark:text-red-500',
                )}
              >
                {fmt.compactCurrency.format(stats.totalProfit)}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">
                {t('common.labels.companies')}
              </span>
              <span className="font-medium">
                {stats.companyCount.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>

      {visibleChildren.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            {locale === 'de' ? 'Untersektoren' : 'Subsectors'}
          </h2>
          <div className="flex flex-col gap-2">
            {visibleChildren.map((child) => (
              <LocaleLink
                key={child.code}
                href={generateIndustryUrl(child.code, child.description)}
                className="group flex items-center py-2 px-4 rounded-xl hover:bg-muted/30 transition-all duration-300 border border-black/5 bg-card/40"
              >
                <div className="flex-grow min-w-0 mr-6">
                  <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    {child.code}. {child.description}
                  </h3>
                </div>
                <div className="hidden lg:flex flex-shrink-0 items-center gap-0 pr-4">
                  <div className="w-[100px] text-right">
                    <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider">
                      {t('common.labels.revenue')}
                    </p>
                    <p className="text-sm font-medium tracking-tight">
                      {fmt.compactCurrency.format(child.totalRevenue)}
                    </p>
                  </div>
                  <div className="w-[80px] text-right">
                    <p className="text-[7px] uppercase font-medium text-muted-foreground tracking-wider">
                      {t('common.labels.companies')}
                    </p>
                    <p className="text-sm font-medium tracking-tight text-foreground/70">
                      {child.companyCount.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="lg:hidden flex items-center gap-2 text-[11px] mr-3">
                  <span className="text-foreground/70">
                    {fmt.compactCurrency.format(child.totalRevenue)}
                  </span>
                  <span className="text-muted-foreground/40">·</span>
                  <span className="text-muted-foreground">
                    {child.companyCount.toLocaleString()}
                  </span>
                </div>
                <div className="size-7 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                  <ArrowRight size={12} />
                </div>
              </LocaleLink>
            ))}
          </div>
        </section>
      )}

      {pieData.length > 1 && (
        <Card variant="grid">
          <CardHeader className="pb-2">
            <CardTitle as="h2">
              {locale === 'de' ? 'Marktanteil' : 'Market Share'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-[200px] h-[200px] shrink-0">
                <ChartContainer config={{}} className="w-full h-full">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius="55%"
                      outerRadius="80%"
                      dataKey="value"
                      strokeWidth={2}
                      stroke="var(--background)"
                      isAnimationActive={false}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-background border border-border rounded-lg p-3 shadow-xl text-xs">
                            <p className="font-semibold text-foreground mb-1 max-w-[180px] truncate">
                              {d.name}
                            </p>
                            <p className="text-muted-foreground">
                              {d.value.toFixed(2)}%{' '}
                              {locale === 'de' ? 'der Branche' : 'of sector'}
                            </p>
                            {d.revenue > 0 && (
                              <p className="text-primary font-medium">
                                {fmt.compactCurrency.format(d.revenue)}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ChartContainer>
              </div>
              <div className="w-full grid grid-cols-1 gap-y-2">
                {pieData.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 min-w-0">
                    {entry.logoUrl ? (
                      <img
                        src={entry.logoUrl}
                        alt=""
                        className="flex-shrink-0 w-4 h-4 rounded-sm object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove(
                            'hidden',
                          );
                        }}
                      />
                    ) : null}
                    <Building2
                      size={14}
                      className={cn(
                        'flex-shrink-0 text-muted-foreground',
                        entry.logoUrl && 'hidden',
                      )}
                    />
                    {entry.isMore && entry.href ? (
                      /* onClick navigation (not <a href>) so crawlers don't
                         discover parameterized /suche URLs (GSC noise) */
                      <button
                        type="button"
                        onClick={() => router.push(entry.href!)}
                        className="text-xs text-foreground/70 truncate flex-grow underline hover:text-primary transition-colors inline-flex items-center gap-1 text-left"
                      >
                        {entry.name}
                        <ArrowUpRight size={12} className="flex-shrink-0" />
                      </button>
                    ) : entry.href ? (
                      <Link
                        href={entry.href}
                        className="text-xs text-foreground/70 truncate flex-grow hover:text-primary hover:underline transition-colors"
                      >
                        {entry.name}
                      </Link>
                    ) : (
                      <span className="text-xs text-foreground/70 truncate flex-grow">
                        {entry.name}
                      </span>
                    )}
                    <span className="flex-shrink-0 text-xs font-semibold text-foreground tabular-nums">
                      {entry.value.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {topCompanies.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            {locale === 'de'
              ? 'Größte Unternehmen der Branche'
              : 'Largest companies in this sector'}
          </h2>
          <ol className="flex flex-col gap-1">
            {topCompanies.map((c, i) => {
              const href =
                c.registerCourt && c.registerType && c.registerId
                  ? generateCompanySlug(
                      {
                        name: c.name,
                        sitz: c.sitz,
                        registerCourt: c.registerCourt,
                        registerType: c.registerType,
                        registerId: c.registerId,
                      },
                      locale as Locale,
                    )
                  : null;
              return (
                <li
                  key={c.euId}
                  className="flex items-center gap-3 py-1.5 px-4 rounded-lg border border-black/5 bg-card/40 min-w-0"
                >
                  <span className="w-6 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {i + 1}.
                  </span>
                  {href ? (
                    <Link
                      href={href}
                      className="flex-grow min-w-0 truncate text-sm font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {c.name}
                    </Link>
                  ) : (
                    <span className="flex-grow min-w-0 truncate text-sm font-medium">
                      {c.name}
                    </span>
                  )}
                  {c.sitz && (
                    <span className="hidden sm:block shrink-0 text-xs text-muted-foreground">
                      {c.sitz}
                    </span>
                  )}
                  <span className="shrink-0 text-xs font-medium tabular-nums">
                    {fmt.compactCurrency.format(c.revenue)}
                  </span>
                  <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {c.marketShare.toFixed(1)}%
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {keywords.length > 0 && (() => {
        const TOP = 8;
        const groups: { cat: 'product' | 'risk' | 'opportunity'; label: string; route: string; color: string }[] = [
          {
            cat: 'product',
            label: locale === 'de' ? 'Produkte' : 'Products',
            route: '/products',
            color: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
          },
          {
            cat: 'risk',
            label: locale === 'de' ? 'Risiken' : 'Risks',
            route: '/risks',
            color: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
          },
          {
            cat: 'opportunity',
            label: locale === 'de' ? 'Chancen' : 'Opportunities',
            route: '/opportunities',
            color: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
          },
        ];
        const sections = groups
          .map((g) => ({
            ...g,
            items: keywords.filter((k) => k.category === g.cat).slice(0, TOP),
          }))
          .filter((g) => g.items.length > 0);
        if (sections.length === 0) return null;
        return (
          <Card variant="grid">
            <CardHeader className="pb-2">
              <CardTitle as="h2">
                {locale === 'de' ? 'Branchen-Keywords' : 'Industry Keywords'}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {sections.map(({ cat, label, items, route, color }) => (
                <div key={cat}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    {label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {/* onClick navigation (not <a href>) so crawlers don't
                        discover the noindexed keyword pages (GSC soft-404
                        noise) */}
                    {items.map((k) => (
                      <button
                        type="button"
                        key={k.keyword}
                        onClick={() =>
                          router.push(
                            localizePathname(
                              `${route}/${encodeURIComponent(k.keyword)}`,
                              locale as Locale,
                            ),
                          )
                        }
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium hover:opacity-80 transition-opacity ${color}`}
                      >
                        {k.keyword}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}
    </Container>
    </>
  );
}
